import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/utils/axios";
import { formatDateTime } from "@/utils/formatDateTime";
import VideoPlayer from "@/components/video/VideoPlayer/VideoPlayer";
import avatarFallbackImg from "@/assets/UI/avatar.png";
import cls from "./UserPostSingle.module.scss";

const REACTIONS = [
  { key: "heart", emoji: "❤️", label: "love" },
  { key: "haha", emoji: "😂", label: "haha" },
  { key: "wow", emoji: "😮", label: "wow" },
  { key: "sexy", emoji: "🔥", label: "sexy" },
  { key: "sad", emoji: "🤌", label: "wtf" },
];

const reactionMeta = REACTIONS.reduce((acc, item) => {
  acc[item.key] = item;
  return acc;
}, {});

const readToken = () => {
  if (typeof window === "undefined") return "";
  return localStorage.getItem("cm_at") || "";
};

const toReadableLabel = (key) =>
  String(key || "")
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();

const isLikelyDate = (value) => {
  if (typeof value !== "string") return false;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed);
};

const UserPostSingle = () => {
  const { userId, postId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await api.get(
          `/v1/admin/members/single/${userId}/posts/${postId}`,
        );

        if (!mounted) return;
        setPayload(res.data || null);
      } catch (err) {
        if (!mounted) return;
        setError(
          err?.response?.data?.message || "Failed to load post details.",
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    run();

    return () => {
      mounted = false;
    };
  }, [userId, postId]);

  const renderDateTime = (value) => {
    const formatted = formatDateTime(value);
    if (!formatted) return "—";
    return `${formatted.day}${formatted.suffix} ${formatted.month} ${formatted.year}, ${formatted.time}`;
  };

  const post = payload?.post || null;
  const postUser = post?.user || payload?.user || null;
  const reactions = useMemo(
    () => payload?.reactions || [],
    [payload?.reactions],
  );
  const viewers = useMemo(() => payload?.viewers || [], [payload?.viewers]);
  const images = useMemo(() => post?.images || [], [post?.images]);
  const accessToken = readToken();
  const canProxyPlay = Boolean(post?.video?.id && accessToken);
  const canDirectPlay = Boolean(post?.video?.signedHlsMasterUrl);
  const hasVideo = canProxyPlay || canDirectPlay;

  const reactionSummary = useMemo(() => {
    const map = {};
    reactions.forEach((item) => {
      const type = item?.type || "unknown";
      map[type] = (map[type] || 0) + 1;
    });
    return Object.entries(map).map(([type, count]) => {
      const meta = reactionMeta[type] || {
        emoji: "💬",
        label: type,
      };
      return {
        key: type,
        emoji: meta.emoji,
        label: meta.label,
        count,
      };
    });
  }, [reactions]);

  const additionalPostDetails = useMemo(() => {
    if (!post || typeof post !== "object") return [];

    const excluded = new Set([
      "title",
      "postLanguage",
      "club",
      "description",
      "createdAt",
      "images",
      "video",
      "user",
    ]);

    return Object.entries(post)
      .filter(([key, value]) => {
        if (excluded.has(key)) return false;
        if (value == null || value === "") return false;

        const isScalar =
          typeof value === "string" ||
          typeof value === "number" ||
          typeof value === "boolean";

        return isScalar;
      })
      .map(([key, value]) => ({
        key,
        label: toReadableLabel(key),
        value:
          isLikelyDate(value) && /at$/i.test(key)
            ? renderDateTime(value)
            : String(value),
      }));
  }, [post]);

  const handleOpenUser = (targetUserId) => {
    if (!targetUserId) return;
    navigate(`/admin/members/single/${targetUserId}`);
  };

  return (
    <div className={cls.page}>
      <div className={cls.bgGlowOne} />
      <div className={cls.bgGlowTwo} />

      <header className={cls.topbar}>
        <div className={cls.topbarLeft}>
          <button
            type="button"
            className={cls.backBtn}
            onClick={() => navigate(`/admin/members/single/posts/${userId}`)}
          >
            <span className={cls.btnIcon}>←</span>
            <span>Back to Posts</span>
          </button>

          <div className={cls.titleWrap}>
            <p className={cls.eyebrow}>Admin Post Viewer</p>
            <h1 className={cls.pageTitle}>Single Post Details</h1>
          </div>
        </div>

        <button
          type="button"
          className={cls.createBtn}
          onClick={() =>
            navigate(`/admin/members/single/posts/${userId}/create`)
          }
        >
          Create New Post
        </button>
      </header>

      {loading && (
        <div className={`${cls.stateBox} ${cls.loadingBox}`}>
          <span className={cls.loader} />
          <span>Loading post...</span>
        </div>
      )}

      {!loading && error && (
        <div className={`${cls.stateBox} ${cls.errorBox}`}>{error}</div>
      )}

      {!loading && !error && post && (
        <>
          <section className={`${cls.card} ${cls.heroCard}`}>
            <div className={cls.heroTop}>
              <div className={cls.heroTitleWrap}>
                <p className={cls.sectionEyebrow}>Overview</p>
                <h2 className={cls.sectionTitle}>Post Details</h2>
              </div>

              <div className={cls.heroBadges}>
                <span className={cls.infoPill}>{post.type || "post"}</span>
                <span className={cls.infoPill}>{post.visibility || "—"}</span>
                <span className={cls.infoPill}>{post.status || "—"}</span>
              </div>
            </div>

            <div className={cls.statsRow}>
              <div className={cls.statCard}>
                <span className={cls.statLabel}>Images</span>
                <strong className={cls.statValue}>{images.length}</strong>
              </div>

              <div className={cls.statCard}>
                <span className={cls.statLabel}>Reactions</span>
                <strong className={cls.statValue}>{reactions.length}</strong>
              </div>

              <div className={cls.statCard}>
                <span className={cls.statLabel}>Viewers</span>
                <strong className={cls.statValue}>{viewers.length}</strong>
              </div>

              <div className={cls.statCard}>
                <span className={cls.statLabel}>Video</span>
                <strong className={cls.statValue}>
                  {hasVideo ? "Yes" : "No"}
                </strong>
              </div>
            </div>

            <div className={cls.detailsGrid}>
              <div className={cls.detailItem}>
                <span className={cls.detailLabel}>Title</span>
                <span className={cls.detailValue}>{post.title || "—"}</span>
              </div>


              <div className={cls.detailItem}>
                <span className={cls.detailLabel}>Club</span>
                <span className={cls.detailValue}>
                  {post.club?.clubName || "—"}
                </span>
              </div>

              <div className={`${cls.detailItem} ${cls.fullRow}`}>
                <span className={cls.detailLabel}>Description</span>
                <div className={cls.descriptionBox}>
                  {post.description || "—"}
                </div>
              </div>
            </div>
          </section>

          <div className={cls.grid}>
            <section className={cls.card}>
              <div className={cls.cardHead}>
                <div>
                  <p className={cls.sectionEyebrow}>Gallery</p>
                  <h2 className={cls.sectionTitle}>Media</h2>
                </div>
              </div>

              {images.length > 0 ? (
                <div className={cls.mediaGrid}>
                  {images.map((img, index) => (
                    <a
                      key={img.key || img.url || index}
                      href={img.url}
                      target="_blank"
                      rel="noreferrer"
                      className={cls.mediaTile}
                    >
                      <img
                        src={img.url}
                        alt={`post-media-${index + 1}`}
                        className={cls.image}
                      />
                      <span className={cls.mediaOverlay}>
                        Image {index + 1}
                      </span>
                    </a>
                  ))}
                </div>
              ) : (
                <div className={cls.emptyBox}>
                  No images uploaded for this post.
                </div>
              )}

              {hasVideo && (
                <div className={cls.videoPlayerWrap}>
                  {canProxyPlay ? (
                    <VideoPlayer
                      token={accessToken}
                      videoId={post.video?.id}
                      mode="proxy"
                      poster={post.video?.posterUrl || undefined}
                      muted={false}
                      controls
                    />
                  ) : (
                    <VideoPlayer
                      hlsMasterUrl={post.video?.signedHlsMasterUrl}
                      mode="cloudfront"
                      poster={post.video?.posterUrl || undefined}
                      muted={false}
                      controls
                    />
                  )}

                  {canDirectPlay && (
                    <a
                      href={post.video.signedHlsMasterUrl}
                      target="_blank"
                      rel="noreferrer"
                      className={cls.videoLink}
                    >
                      <span className={cls.videoDot} />
                      <span>Open Video Stream</span>
                    </a>
                  )}
                </div>
              )}
            </section>

            <section className={cls.card}>
              <div className={cls.cardHead}>
                <div>
                  <p className={cls.sectionEyebrow}>Engagement</p>
                  <h2 className={cls.sectionTitle}>
                    Reactions ({reactions.length})
                  </h2>
                </div>
              </div>

              {reactionSummary.length > 0 && (
                <div className={cls.summaryChips}>
                  {reactionSummary.map((item) => (
                    <span key={item.key} className={cls.summaryChip}>
                      <span>{item.emoji}</span>
                      <span>{item.label}</span>
                      <strong>{item.count}</strong>
                    </span>
                  ))}
                </div>
              )}

              {reactions.length > 0 ? (
                <ul className={cls.peopleList}>
                  {reactions.map((r, i) => {
                    const reaction = reactionMeta[r.type] || {
                      emoji: "💬",
                      label: r.type || "unknown",
                    };

                    return (
                      <li key={`${r.userId}-${i}`} className={cls.personRow}>
                        <button
                          type="button"
                          onClick={() => handleOpenUser(r.userId)}
                          className={cls.userBtn}
                        >
                          <img
                            src={
                              r.avatarUrl ||
                              r.user?.avatarUrl ||
                              avatarFallbackImg
                            }
                            alt={r.username || r.user?.username || "user"}
                            className={cls.avatar}
                          />

                          <span className={cls.userTextWrap}>
                            <span className={cls.username}>
                              @{r.username || r.user?.username || "unknown"}
                            </span>
                            <span className={cls.subText}>Member reacted</span>
                          </span>
                        </button>

                        <span className={cls.reactionTag}>
                          <span className={cls.reactionEmoji}>
                            {reaction.emoji}
                          </span>
                          <span>{reaction.label}</span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className={cls.emptyBox}>No reactions yet.</div>
              )}
            </section>

            <section className={`${cls.card} ${cls.fullWidthCard}`}>
              <div className={cls.cardHead}>
                <div>
                  <p className={cls.sectionEyebrow}>Post Reach</p>
                  <h2 className={cls.sectionTitle}>
                    Insights / Viewers ({viewers.length})
                  </h2>
                </div>
              </div>

              {viewers.length > 0 ? (
                <ul className={cls.peopleList}>
                  {viewers.map((viewer, i) => (
                    <li key={`${viewer.userId}-${i}`} className={cls.personRow}>
                      <button
                        type="button"
                        onClick={() => handleOpenUser(viewer.userId)}
                        className={cls.userBtn}
                      >
                        <img
                          src={
                            viewer.avatarUrl ||
                            viewer.user?.avatarUrl ||
                            avatarFallbackImg
                          }
                          alt={
                            viewer.username || viewer.user?.username || "user"
                          }
                          className={cls.avatar}
                        />

                        <span className={cls.userTextWrap}>
                          <span className={cls.username}>
                            @
                            {viewer.username ||
                              viewer.user?.username ||
                              "unknown"}
                          </span>
                          <span className={cls.subText}>Viewed this post</span>
                        </span>
                      </button>

                      <span className={cls.meta}>
                        {renderDateTime(viewer.lastViewedAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className={cls.emptyBox}>
                  No viewer insights available.
                </div>
              )}
            </section>
          </div>
          <section className={`${cls.card} ${cls.heroCard}`}>

            <div className={cls.detailsGrid}>
             

              <div className={cls.detailItem}>
                <span className={cls.detailLabel}>Language</span>
                <span className={cls.detailValue}>
                  {post.postLanguage || "—"}
                </span>
              </div>

            

              <div className={cls.detailItem}>
                <span className={cls.detailLabel}>Created</span>
                <span className={cls.detailValue}>
                  {renderDateTime(post.createdAt)}
                </span>
              </div>

              <div className={cls.detailItem}>
                <span className={cls.detailLabel}>User</span>
                {postUser ? (
                  <button
                    type="button"
                    className={cls.detailUserBtn}
                    onClick={() => handleOpenUser(postUser.id || postUser._id)}
                  >
                    <img
                      src={postUser.avatarUrl || avatarFallbackImg}
                      alt={postUser.username || "user"}
                      className={cls.avatar}
                    />
                    <span className={cls.username}>
                      @{postUser.username || "unknown"}
                    </span>
                  </button>
                ) : (
                  <span className={cls.detailValue}>—</span>
                )}
              </div>

              {additionalPostDetails.map((detail) => (
                <div key={detail.key} className={cls.detailItem}>
                  <span className={cls.detailLabel}>{detail.label}</span>
                  <span className={cls.detailValue}>{detail.value}</span>
                </div>
              ))}

              
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default UserPostSingle;
